import React, { useContext, useEffect, useState } from 'react'
import BigModal from '../../../../components/Modals/BigModal'
import {
  Button,
  ButtonGroup,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

const PAYMENT_METHODS = ['Bank Transfer', 'Card', 'Cash', 'Other']
import { DetailRow } from '../../../../components/Cards/DetailsBox'
import moment from 'moment'
import AnDateField from '../../../../components/DateTime/DateField'
import API from '../../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../../App'

const RecordPayment = ({
  invoice,
  setInvoice,
  open,
  onClose,
  amountOutstanding,
}) => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const [amount, setAmount] = useState(
    parseFloat(amountOutstanding).toFixed(2) ?? 0,
  )
  const [datePaid, setDatePaid] = useState(moment())
  const [method, setMethod] = useState('Bank Transfer')
  const [note, setNote] = useState('')
  const { setToast } = useContext(GlobalContext)
  const customerName =
    invoice?.organisation?.orgName || invoice?.patientCustomer?.name || '—'
  const sourceLabel =
    invoice?.source === 'PTS'
      ? 'PTS Booking'
      : invoice?.event
        ? 'Event'
        : 'Source'
  const sourceValue =
    invoice?.source === 'PTS'
      ? invoice?.ptsBooking?.bookingRef || '—'
      : invoice?.event?.title || '—'

  useEffect(() => {
    if (!open) {
      setAmount(parseFloat(amountOutstanding).toFixed(2) ?? 0)
      setDatePaid(moment())
      setMethod('Bank Transfer')
      setNote('')
    }
  }, [open, amountOutstanding])

  const handleAmountChange = (e) => {
    setAmount(e.target.value)
  }

  const handleAmountBlur = () => {
    setAmount(parseFloat(amount).toFixed(2))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const parsedAmount = parseFloat(amount).toFixed(2)
    API.post('/finance/invoice/payment', {
      invoiceID: invoice._id,
      amount: parsedAmount,
      datePaid: moment(datePaid).toDate(),
      method,
      note,
    })
      .then((res) => {
        if (res.status === 200) {
          onClose()
          setToast({
            message: 'Payment Recorded',
          })
          // The route returns the saved invoice (with the recomputed
          // status) - use it rather than patching state locally
          setInvoice((prev) => ({ ...prev, ...res.data }))
        } else {
          setToast({
            message:
              res?.data?.error || 'Unable to record payment. Please try again later',
            severity: 'error',
          })
        }
      })
      .catch(() => {
        setToast({
          message: 'Unable to record payment. Please try again later.',
          severity: 'error',
        })
      })
  }

  return (
    <BigModal open={open} onClose={() => onClose()}>
      <Stack gap={2} component="form" onSubmit={handleSubmit}>
        <Typography variant="title3">
          Record Payment for Invoice {invoice?.reference}
        </Typography>
        <Stack direction={{ xs: 'column', lg: 'row' }} gap={{ xs: 1, lg: 4 }}>
          <DetailRow label="Customer" value={customerName} />
          <DetailRow label={sourceLabel} value={sourceValue} />
        </Stack>
        <TextField
          type="number"
          inputProps={{
            min: 0,
            step: 0.01,
          }}
          value={amount}
          onChange={handleAmountChange}
          onBlur={handleAmountBlur}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {orgSettings?.currency ?? '£'}
              </InputAdornment>
            ),
          }}
          label="Amount Paid"
          required
        />
        <AnDateField
          date={datePaid}
          onChange={(newDate) => setDatePaid(newDate)}
          label="Date Paid"
          disableFuture
          required
        />
        <TextField
          select
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          label="Payment Method"
          required
        >
          {PAYMENT_METHODS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          multiline
          value={note}
          onChange={(e) => setNote(e.target.value)}
          label="Note"
        />
        <ButtonGroup fullWidth>
          <Button variant="contained" color="anRed" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button variant="contained" color="anGreen" type="submit">
            Submit
          </Button>
        </ButtonGroup>
      </Stack>
    </BigModal>
  )
}

export default RecordPayment
